import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";

const Rbpi = ({ onStatusChange }) => {
  const [licensePlateId, setLicensePlateId] = useState("");
  const [manualPlateId, setManualPlateId] = useState("");
  const [guardianData, setGuardianData] = useState([]);
  const [guestData, setGuestData] = useState([]);
  const [studentData, setStudentData] = useState([]);
  const [selectedGuardianOrGuest, setSelectedGuardianOrGuest] = useState(null);
  const [error, setError] = useState("");
  const [disabledButtons, setDisabledButtons] = useState({});


  const fetchGuardianAndStudentData = async (plateId) => {
    console.log("🚀 Fetching data for license plate:", plateId);
    setError("");
    setGuardianData([]);
    setGuestData([]);
    setStudentData([]);

    try {
      const plateDocRef = doc(db, "license_plates", plateId);
      const plateDocSnap = await getDoc(plateDocRef);

      console.log("📄 License plate document exists:", plateDocSnap.exists());
      if (!plateDocSnap.exists()) {
        setError("License plate not found.");
        return;
      }

      const plateData = plateDocSnap.data();
      console.log("📄 License plate data:", plateData);

      const { guardian_ids: guardianIds, group_id: groupId } = plateData;
      console.log("👥 Guardian IDs:", guardianIds);
      console.log("🏢 Group ID:", groupId);

      const guardianPromises = guardianIds.map(async (guardianId) => {
        const guardianDocRef = doc(db, "guardians", guardianId);
        const guardianDocSnap = await getDoc(guardianDocRef);
        console.log(`👤 Guardian ${guardianId} exists:`, guardianDocSnap.exists());
        return guardianDocSnap.exists() ? { id: guardianId, ...guardianDocSnap.data() } : null;
      });

      const groupDocRef = doc(db, "groups", groupId);
      const groupDocSnap = await getDoc(groupDocRef);
      console.log("🏢 Group document exists:", groupDocSnap.exists());

      let guests = [];
      let students = [];

      if (groupDocSnap.exists()) {
        const groupData = groupDocSnap.data();
        console.log("🏢 Group data:", groupData);
        const { guest_ids = [], student_ids = [] } = groupData;

        console.log("👥 Guest IDs:", guest_ids);
        console.log("👨‍🎓 Student IDs:", student_ids);

        const guestPromises = guest_ids.map(async (guestId) => {
          const guestDocRef = doc(db, "guests", guestId);
          const guestDocSnap = await getDoc(guestDocRef);
          console.log(`👤 Guest ${guestId} exists:`, guestDocSnap.exists());
          return guestDocSnap.exists() ? { id: guestId, ...guestDocSnap.data() } : null;
        });

        const studentPromises = student_ids.map(async (studentId) => {
          const studentDocRef = doc(db, "students", studentId);
          const studentDocSnap = await getDoc(studentDocRef);
          console.log(`👨‍🎓 Student ${studentId} exists:`, studentDocSnap.exists());
          if (studentDocSnap.exists()) {
            const studentData = studentDocSnap.data();
            console.log(`👨‍🎓 Student ${studentId} data:`, studentData);
            return { id: studentId, ...studentData };
          }
          return null;
        });

        guests = await Promise.all(guestPromises);
        students = await Promise.all(studentPromises);
      }

      const guardians = await Promise.all(guardianPromises);

      console.log("📊 Final data sets:");
      console.log("👥 Guardians:", guardians.filter(Boolean));
      console.log("👥 Guests:", guests.filter(Boolean));
      console.log("👨‍🎓 Students:", students.filter(Boolean));

      setGuardianData(guardians.filter(Boolean));
      setGuestData(guests.filter(Boolean));
      setStudentData(students.filter(Boolean));
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError("An error occurred while fetching data.");
    }
  };

  const handleSignOut = async (studentId) => {
    console.log("🚀 Starting sign out process for student:", studentId);
    console.log("👤 Selected guardian/guest:", selectedGuardianOrGuest);

    if (!selectedGuardianOrGuest) {
      setError("Please select a guardian or guest before signing out.");
      return;
    }

    const student = studentData.find((s) => s.id === studentId);
    console.log("👨‍🎓 Student to sign out:", student);
    
    if (!student) {
      console.error("❌ Student not found in studentData");
      setError("Student not found.");
      return;
    }

    if (!student.present) {
      console.log("⚠️ Student already picked up");
      setError("This student has already been signed out.");
      return;
    }

    try {
      const timestamp = new Date().toISOString();
      console.log("⏰ Timestamp:", timestamp);

      // Update student's presence status
      const studentDocRef = doc(db, "students", studentId);
      console.log("📝 Updating student document:", studentId);
      
      await updateDoc(studentDocRef, {
        present: false
      });

      // Update pickup logs
      const logDocRef = doc(db, "pickup_logs", studentId);
      const logDocSnap = await getDoc(logDocRef);

      const newPickup = {
        guardian_id: selectedGuardianOrGuest.id,
        license_plate_id: licensePlateId,
        timestamp: timestamp
      };

      if (logDocSnap.exists()) {
        const existingData = logDocSnap.data();
        await updateDoc(logDocRef, {
          pickups: [...existingData.pickups, newPickup]
        });
      } else {
        await setDoc(logDocRef, {
          pickups: [newPickup]
        });
      }

      // Update local state
      console.log("🔄 Updating local state");
      setStudentData((prev) =>
        prev.map((s) => (s.id === studentId ? { ...s, present: false } : s))
      );
      
      // Disable the button for the signed-out student
      setDisabledButtons((prev) => ({ ...prev, [studentId]: true }));

      console.log("✅ Sign out process completed successfully");
      setError("");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      console.error("❌ Error signing out student:", err);
      setError("Error signing out student: " + err.message);
    }
  };

  useEffect(() => {
    console.log("🔄 License plate ID changed:", licensePlateId);
    if (licensePlateId) {
      fetchGuardianAndStudentData(licensePlateId);
    }
  }, [licensePlateId]);

  const handleManualSubmit = () => {
    console.log("🚀 Manual submit with plate:", manualPlateId);
    if (manualPlateId.trim()) {
      setLicensePlateId(manualPlateId.trim().toUpperCase());
    } else {
      setError("Please enter a license plate.");
    }
  };

  console.log("🔄 Current render state:");
  console.log("- License Plate:", licensePlateId);
  console.log("- Manual Plate:", manualPlateId);
  console.log("- Selected Guardian/Guest:", selectedGuardianOrGuest);
  console.log("- Guardian Data Length:", guardianData.length);
  console.log("- Guest Data Length:", guestData.length);
  console.log("- Student Data Length:", studentData.length);
  console.log("- Error:", error);

  return (
    <div className="license-plate-rec w-full p-4">
      <h2 className="text-lg font-semibold mb-4">License Plate Detection</h2>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Enter License Plate"
          value={manualPlateId}
          onChange={(e) => setManualPlateId(e.target.value)}
          className="border p-2 rounded w-full"
        />
        <button 
          onClick={handleManualSubmit} 
          className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Submit
        </button>
      </div>

      {(guardianData.length > 0 || guestData.length > 0) && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Select a Guardian or Guest:</h3>
          <select
            className="border p-2 rounded w-full"
            value={selectedGuardianOrGuest?.id || ""}
            onChange={(e) => {
              console.log("👤 Guardian/Guest selected:", e.target.value);
              const selected = [...guardianData, ...guestData].find(p => p.id === e.target.value);
              console.log("👤 Selected data:", selected);
              setSelectedGuardianOrGuest(selected || null);
            }}
          >
            <option value="">Select...</option>
            {guardianData.length > 0 && (
              <optgroup label="Guardians">
                {guardianData.map((guardian) => (
                  <option key={guardian.id} value={guardian.id}>
                    {guardian.name} ({guardian.relationship})
                  </option>
                ))}
              </optgroup>
            )}
            {guestData.length > 0 && (
              <optgroup label="Guests">
                {guestData.map((guest) => (
                  <option key={guest.id} value={guest.id}>
                    {guest.name} ({guest.relationship})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      )}

{studentData.length > 0 && (
        <div className="mt-4">
          <h4 className="text-lg font-semibold mb-2">Students:</h4>
          <ul>
            {studentData.map((student) => {
              console.log(`👨‍🎓 Rendering student ${student.id}:`, student);
              return (
                <li key={student.id} className="mb-2 p-2 border rounded flex justify-between items-center">
                  <span>{student.name} - Grade {student.grade}</span>
                  <button
                    onClick={() => handleSignOut(student.id)}
                    disabled={!selectedGuardianOrGuest || !student.present || disabledButtons[student.id]}
                    className={`px-4 py-2 text-white rounded ${
                      !student.present || disabledButtons[student.id]
                        ? "bg-gray-300 cursor-not-allowed"
                        : !selectedGuardianOrGuest
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-red-500 hover:bg-red-600"
                    }`}
                  >
                    {!student.present ? "Picked Up" : "Sign Out"}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {error && <p className="text-red-500 mt-2">{error}</p>}
    </div>
  );
};

export default Rbpi;
